import { View, StyleSheet, Text, ScrollView, Image, TouchableOpacity, Linking } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { BottomSheet } from '../common/BottomSheet';
import { colors } from '../../utils/colors';
import { detectHashtags, detectMentions, detectUrls } from '../../utils/text';

interface Comment {
  id: string;
  authorName: string;
  authorHandle: string;
  authorAvatar?: string;
  authorDid: string;
  text: string;
  createdAt: string;
  likeCount: number;
  uri: string;
  cid: string;
  likeUri?: string;
  facets?: any[];
  embed?: any;
}

interface CommentsModalProps {
  visible: boolean;
  onClose: () => void;
  comments: Comment[];
  onLikeComment?: (comment: Comment) => void | Promise<void>;
  onReplyComment?: (comment: Comment) => void;
}

const CommentsModal: React.FC<CommentsModalProps> = ({ visible, onClose, comments, onLikeComment, onReplyComment }) => {
  // テキストをパースしてリッチ表示用のコンポーネントに変換
  const renderRichText = (text: string) => {
    const hashtags = detectHashtags(text);
    const mentions = detectMentions(text);
    const urls = detectUrls(text);

    const parts: { text: string; type: 'text' | 'hashtag' | 'mention' | 'url' }[] = [];
    let lastIndex = 0;
    const allMatches: { start: number; end: number; text: string; type: 'hashtag' | 'mention' | 'url' }[] = [];

    // ハッシュタグの位置を特定（重複を避けるため、正規表現ではなくtext.indexOfを慎重に使用するか、正規表現で直接取得する）
    // ここでは簡易的にPostCardと同様の実装を行う
    hashtags.forEach(tag => {
      let index = text.indexOf('#' + tag, lastIndex);
      if (index !== -1) {
        allMatches.push({ start: index, end: index + tag.length + 1, text: '#' + tag, type: 'hashtag' });
      }
    });

    mentions.forEach(mention => {
      let index = text.indexOf('@' + mention, lastIndex);
      if (index !== -1) {
        allMatches.push({ start: index, end: index + mention.length + 1, text: '@' + mention, type: 'mention' });
      }
    });

    urls.forEach(url => {
      let index = text.indexOf(url, lastIndex);
      if (index !== -1) {
        allMatches.push({ start: index, end: index + url.length, text: url, type: 'url' });
      }
    });

    allMatches.sort((a, b) => a.start - b.start);

    lastIndex = 0;
    allMatches.forEach(match => {
      if (match.start > lastIndex) {
        parts.push({ text: text.substring(lastIndex, match.start), type: 'text' });
      }
      parts.push({ text: match.text, type: match.type });
      lastIndex = match.end;
    });

    if (lastIndex < text.length) {
      parts.push({ text: text.substring(lastIndex), type: 'text' });
    }

    if (parts.length === 0) return <Text style={styles.commentText}>{text}</Text>;

    return (
      <Text style={styles.commentText}>
        {parts.map((part, index) => {
          switch (part.type) {
            case 'hashtag':
              return <Text key={index} style={styles.hashtag}>{part.text}</Text>;
            case 'mention':
              return <Text key={index} style={styles.mention}>{part.text}</Text>;
            case 'url':
              return (
                <Text key={index} style={styles.link} onPress={() => Linking.openURL(part.text)}>
                  {part.text}
                </Text>
              );
            default:
              return <Text key={index}>{part.text}</Text>;
          }
        })}
      </Text>
    );
  };

  const renderEmbeds = (embed: any) => {
    if (!embed) return null;

    // 画像の場合
    if (embed.$type === 'app.bsky.embed.images#view' || embed.images) {
      const images = embed.images || [];
      return (
        <View style={styles.imagesContainer}>
          {images.map((img: any, idx: number) => (
            <Image 
              key={idx} 
              source={{ uri: img.thumb || img.fullsize }} 
              style={[
                styles.embeddedImage,
                images.length === 1 ? styles.imageSingle : styles.imageMultiple
              ]} 
              resizeMode="cover"
            />
          ))}
        </View>
      );
    }

    // 外部サイトの場合
    if (embed.$type === 'app.bsky.embed.external#view' || embed.external) {
      const external = embed.external;
      return (
        <TouchableOpacity 
          style={styles.externalContainer} 
          onPress={() => Linking.openURL(external.uri)}
        >
          {external.thumb && (
            <Image source={{ uri: external.thumb }} style={styles.externalThumb} />
          )}
          <View style={styles.externalContent}>
            <Text style={styles.externalTitle} numberOfLines={1}>{external.title}</Text>
            <Text style={styles.externalDescription} numberOfLines={2}>{external.description}</Text>
          </View>
        </TouchableOpacity>
      );
    }

    return null;
  };

  return (
    <BottomSheet visible={visible} onClose={onClose}>
      <View style={styles.container}>
        {/* ヘッダー */}
        <View style={styles.header}>
          <Text style={styles.headerTitle}>コメント {comments.length}</Text>
          <TouchableOpacity onPress={onClose} style={styles.closeButton}>
            <Ionicons name="close" size={24} color="#aaa" />
          </TouchableOpacity>
        </View>

        {/* コメントリスト */}
        <ScrollView style={styles.commentsList}>
          {comments.length > 0 ? (
            comments.map((comment) => (
              <View key={comment.id} style={styles.commentItem}>
                <View style={styles.commentHeader}>
                  {comment.authorAvatar ? (
                    <Image source={{ uri: comment.authorAvatar }} style={styles.avatar} />
                  ) : (
                    <View style={[styles.avatar, { backgroundColor: '#333' }]} />
                  )}
                  <View style={styles.commentInfo}>
                    <View style={styles.authorRow}>
                      <Text style={styles.authorName}>{comment.authorName}</Text>
                      <Text style={styles.commentTime}>{comment.createdAt}</Text>
                    </View>
                    <Text style={styles.authorHandle}>@{comment.authorHandle}</Text>
                  </View>
                </View>
                
                {renderRichText(comment.text)}
                {renderEmbeds(comment.embed)}
                
                {/* コメントアクション */}
                <View style={styles.commentActions}>
                  <TouchableOpacity 
                    style={styles.actionButton}
                    onPress={() => onLikeComment?.(comment)}
                  >
                    <Ionicons 
                      name={comment.likeUri ? "thumbs-up" : "thumbs-up-outline"} 
                      size={16} 
                      color={comment.likeUri ? colors.primary : "#aaa"} 
                    />
                    <Text style={[styles.actionText, comment.likeUri ? { color: colors.primary } : null]}>
                      {comment.likeCount > 0 ? comment.likeCount : ''}
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity 
                    style={styles.actionButton}
                    onPress={() => onReplyComment?.(comment)}
                  >
                    <Ionicons name="chatbubble-outline" size={16} color="#aaa" />
                    <Text style={styles.actionText}>返信</Text>
                  </TouchableOpacity>
                </View>
              </View>
            ))
          ) : (
            <View style={styles.noCommentsContainer}>
              <Ionicons name="chatbubble-outline" size={48} color="#555" />
              <Text style={styles.noCommentsText}>コメントはまだありません</Text>
              <Text style={styles.noCommentsSubtext}>最初にコメントしましょう</Text>
            </View>
          )}
        </ScrollView>
      </View>
    </BottomSheet>
  );
};

const styles = StyleSheet.create({
  container: {
    maxHeight: '80%',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  closeButton: {
    padding: 4,
  },
  headerTitle: {
    color: colors.text,
    fontSize: 18,
    fontWeight: 'bold',
    flex: 1,
  },
  commentsList: {
    maxHeight: 500,
  },
  commentItem: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  commentHeader: {
    flexDirection: 'row',
    marginBottom: 8,
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
    marginRight: 12,
  },
  commentInfo: {
    flex: 1,
  },
  authorRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 2,
  },
  authorName: {
    color: colors.text,
    fontSize: 13,
    fontWeight: '600',
    marginRight: 8,
  },
  authorHandle: {
    color: colors.textSecondary,
    fontSize: 11,
  },
  commentTime: {
    color: colors.textSecondary,
    fontSize: 12,
  },
  commentText: {
    color: colors.text,
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 8,
  },
  hashtag: {
    color: colors.primary,
  },
  mention: {
    color: '#3e9ced',
  },
  link: {
    color: '#3e9ced',
    textDecorationLine: 'underline',
  },
  imagesContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
    marginBottom: 12,
  },
  embeddedImage: {
    borderRadius: 8,
    backgroundColor: colors.background,
  },
  imageSingle: {
    width: '100%',
    aspectRatio: 16 / 9,
  },
  imageMultiple: {
    width: '48%',
    aspectRatio: 1,
  },
  externalContainer: {
    flexDirection: 'row',
    backgroundColor: colors.background,
    borderRadius: 8,
    overflow: 'hidden',
    marginBottom: 12,
    borderWidth: 1,
    borderColor: colors.border,
  },
  externalThumb: {
    width: 80,
    height: 80,
  },
  externalContent: {
    flex: 1,
    padding: 8,
    justifyContent: 'center',
  },
  externalTitle: {
    color: colors.text,
    fontSize: 13,
    fontWeight: 'bold',
    marginBottom: 2,
  },
  externalDescription: {
    color: colors.textSecondary,
    fontSize: 11,
  },
  commentActions: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 4,
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 20,
    paddingVertical: 4,
  },
  actionText: {
    color: colors.textSecondary,
    fontSize: 11,
    marginLeft: 4,
  },
  noCommentsContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 80,
  },
  noCommentsText: {
    color: colors.text,
    fontSize: 16,
    fontWeight: '600',
    marginTop: 16,
  },
  noCommentsSubtext: {
    color: colors.textSecondary,
    fontSize: 14,
    marginTop: 4,
  },
});

export default CommentsModal;

